import {
  Dispatch,
  FC,
  forwardRef,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import TextInput from "../../../UI/Inputs/TextInput";
import DatePicker from "react-datepicker";
import ChevronSmall from "../../../UI/Icons/ChevronSmall";
import { useAppSelector as useSelector } from "../../../../store";
import {
  TCandidateTimesheetCentresList,
  TTimesheetSort,
} from "../../../../store/timesheet/types";
import Checkbox from "../../../UI/Inputs/Checkbox";
import SortAndFilter from "../../../UI/Icons/SortAndFilter";
import SortFilterDropdown, { SortOrder } from "../../../UI/SortFilterDropdown";
import { timesheetFilterOptions } from "../../../../models/vacancy";

interface ListFilterProps {
  setSearchInput: Dispatch<SetStateAction<string>>;
  selectedStartDate: string;
  setSelectedStartDate: Dispatch<SetStateAction<string>>;
  selectedEndDate: string;
  setSelectedEndDate: Dispatch<SetStateAction<string>>;
  selectedCentre: Array<number>;
  setSelectedCentre: Dispatch<SetStateAction<Array<number>>>;
  setHasUserSelectedRange: Dispatch<SetStateAction<boolean>>;
  handleSortChange?: (sortBy: TTimesheetSort) => void;
}

const ListFilter: FC<ListFilterProps> = ({
  setSearchInput,
  selectedStartDate,
  setSelectedStartDate,
  selectedEndDate,
  setSelectedEndDate,
  selectedCentre,
  setSelectedCentre,
  setHasUserSelectedRange,
  handleSortChange,
}) => {
  const allCentersList = useSelector((state) => state.timesheet.allCentersList);

  const datePickerRef = useRef<any>(null);
  const [selectedDates, setSelectedDates] = useState<
    [Date | null, Date | null]
  >([new Date(selectedStartDate), new Date(selectedEndDate)]);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [openCalendarDropdown, setOpenCalendarDropdown] =
    useState<boolean>(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  /** Update the values in the array for centres */
  const handleCheckBox = (value: number) => {
    setSelectedCentre(
      (prevValue: number[]) =>
        prevValue.includes(Number(value))
          ? prevValue.filter((item) => item !== Number(value)) // Deselect
          : [...prevValue, Number(value)] // Select
    );
  };

  /**
   * Toggles the state of a dropdown menu based on the provided index.
   * If the dropdown corresponding to the given index is already open, it will be closed.
   * Otherwise, it will open the dropdown for the specified index and close any other open dropdowns.
   *
   * @param index - The index of the dropdown to toggle.
   */
  const toggleDropdown = (index: number) => {
    setOpenDropdown((prevIndex) => (prevIndex === index ? null : index));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const filterMenu = document.querySelector(".filter-menu");
      if (filterMenu && !filterMenu.contains(event.target as Node)) {
        toggleDropdown(1); // Close the dropdown
      }

      const CalendarMenu = document.querySelector(".calendar-menu");
      if (CalendarMenu && !CalendarMenu.contains(event.target as Node)) {
        setOpenCalendarDropdown(false); // Close the dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatDate = (date: Date | null) =>
    date
      ? date
          .toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
          .split("-")
          .reverse()
          .join("-") // "yyyy-MM-dd"
      : "";

  const handleCalendar = () => {
    datePickerRef.current?.setOpen(true);
    setOpenCalendarDropdown(true);
  };

  const CustomReadonlyInput = forwardRef(
    ({ value, onClick }: any, ref: any) => (
      <input
        ref={ref}
        value={value}
        onClick={onClick}
        readOnly // 👈 prevents cursor and manual typing
        style={{
          cursor: "pointer",
        }}
      />
    )
  );

  const handleFilterChange = () => {
    setShowMobileFilters((prev) => !prev);
  };

  const handleLocalSortChange = (field: string, order: SortOrder) => {
    timesheetFilterOptions.filter((option) => {
      if (option.label === field) {
        handleSortChange && handleSortChange(option.value as TTimesheetSort);
        return true;
      }
      return false;
    });
  };

  return (
    <>
      <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between">
        <div className="form-group w-200px mobile-w-100 input-icon icon-left">
          <TextInput
            placeholder="Search..."
            field="search"
            onChange={(e) => setSearchInput(e.target.value)}
            showSearchIcon={true}
            floatingLabel={false}
          />
        </div>

        {/* Mobile Sort and Filter Toggle */}
        <div className="d-flex d-lg-none flex-column">
          <div className="h-10px"></div>
          <div
            className="d-flex gap-5 align-items-center justify-content-center"
            style={{ cursor: "pointer" }}
            onClick={handleFilterChange}
          >
            <SortAndFilter />
            <div className="fs-13 fw-700 text-black">Sort and Filter</div>
          </div>
          <div className="h-10px"></div>
          <div className="bg-off-white min-h-1px"></div>
        </div>

        <div
          className={`flex-column flex-lg-row gap-20 gap-mobile-10 align-items-lg-center fs-13 fw-400
          ${showMobileFilters ? "d-flex" : "d-none"} d-lg-flex`}
        >
          <div className="position-relative">
            <div
              className="d-flex gap-3 align-items-center cursor-pointer"
              onClick={() => toggleDropdown(1)}
            >
              <div className="fs-13 fw-400 text-black">Centres</div>
              <div className="fs-13 fw-700 text-black">
                {selectedCentre?.length > 0
                  ? selectedCentre?.length + " Centres selected"
                  : "All"}
              </div>
              <ChevronSmall isOpen={openDropdown === 1 ? false : true} />
            </div>
            {openDropdown === 1 && (
              <div className="position-absolute end-0 d-flex flex-column dropdown-menu w-266px">
                <div className="filter-menu">
                  {allCentersList?.length > 0 ? (
                    allCentersList?.map(
                      (centre: TCandidateTimesheetCentresList) => {
                        return (
                          <div
                            className="theme-check dropdown-item"
                            key={centre.id}
                          >
                            <Checkbox
                              themeCheck={true}
                              className="theme-check-input"
                              id={`idx_${centre.id}`}
                              checked={selectedCentre.includes(centre.id)}
                              label={centre?.business_name ?? ""}
                              onChange={() => handleCheckBox(centre.id)}
                            />
                          </div>
                        );
                      }
                    )
                  ) : (
                    <div className="d-flex flex-column align-items-center theme-check dropdown-item">
                      No Centres Found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="position-relative d-flex gap-3 align-items-center">
            <div className="fs-13 fw-400 text-black">Date Range</div>
            <div className="text-only d-flex align-items-center fs-13 fw-700 text-black calendar-menu">
              <DatePicker
                ref={datePickerRef}
                selected={selectedDates[0]}
                onChange={(dates: [Date | null, Date | null]) => {
                  const [start, end] = dates;
                  setSelectedDates([start, end]);

                  setSelectedStartDate(formatDate(start));
                  setSelectedEndDate(formatDate(end));

                  if (start && end) {
                    setHasUserSelectedRange(true);
                    setOpenCalendarDropdown(false);
                  }
                }}
                onCalendarClose={() => {
                  if (selectedDates[0] && !selectedDates[1]) {
                    setSelectedDates([selectedDates[0], selectedDates[0]]);
                  }
                }}
                startDate={selectedDates[0]}
                endDate={selectedDates[1]}
                maxDate={new Date()}
                selectsRange
                dateFormat="dd MMM yyyy"
                popperPlacement="bottom-start"
                customInput={<CustomReadonlyInput />}
              />
              <div onClick={handleCalendar}>
                <ChevronSmall isOpen={!openCalendarDropdown} />
              </div>
            </div>
          </div>
        </div>

        {/** Mobile Sort */}
        <div
          className={`d-block d-lg-none ${
            showMobileFilters ? "d-flex" : "d-none"
          }`}
        >
          <SortFilterDropdown
            sortFields={["Centre", "Job Timeframe"]}
            defaultField="Job Timeframe"
            onChange={handleLocalSortChange}
          />
        </div>
      </div>
    </>
  );
};

export default ListFilter;
